import type {
  BogglePlaygroundMeterBurstSettings,
  BogglePlaygroundMeterSettings,
  BogglePlaygroundMeterWordChangeSettings,
} from '~/boggle/config/playground'
import {
  findEnabledMeterBurstByTrigger,
  getActiveMeterBurst,
} from '~/boggle/lib/meterBursts'

/** Cubic ease-in: rate scales as `(elapsed / ramp)^3`, staying near zero early in the ramp. */
const METER_BURST_DRAIN_RAMP_POWER = 3

export type BoggleMeterState = {
  activeBurstId: string | null
  burstActive: boolean
  /** Elapsed playing time since the current meter burst window began. */
  burstElapsedMs: number
  overmeterValue: number
  tileSpawnElapsedMs: number
  value: number
}

export function createBoggleMeterState(
  settings: BogglePlaygroundMeterSettings,
): BoggleMeterState {
  return {
    activeBurstId: null,
    burstActive: false,
    burstElapsedMs: 0,
    overmeterValue: 0,
    tileSpawnElapsedMs: 0,
    value: clamp(settings.startingValue, 0, settings.capacity),
  }
}

export function getMeterBurstDrainRampMs(
  drain: Pick<BogglePlaygroundMeterBurstSettings['drain'], 'drainRampSeconds'>,
) {
  return Math.max(0, drain.drainRampSeconds) * 1000
}

/** Drain budget after subtracting the cubic ramp's average rate window from `durationSeconds`. */
export function getMeterBurstEffectiveDrainSeconds(
  drain: Pick<
    BogglePlaygroundMeterBurstSettings['drain'],
    'drainRampSeconds' | 'durationSeconds'
  >,
) {
  const rampSeconds = drain.drainRampSeconds
  const rampShare =
    rampSeconds *
    (METER_BURST_DRAIN_RAMP_POWER / (METER_BURST_DRAIN_RAMP_POWER + 1))
  return Math.max(1e-9, drain.durationSeconds - rampShare)
}

function meterBurstRampDecayBetweenSeconds(
  fromSec: number,
  toSec: number,
  maxDecayPerSecond: number,
  rampSec: number,
) {
  if (toSec <= fromSec || maxDecayPerSecond <= 0 || rampSec <= 0) {
    return 0
  }

  const power = METER_BURST_DRAIN_RAMP_POWER
  const rampSecPower = rampSec ** power
  return (
    (maxDecayPerSecond / (rampSecPower * (power + 1))) *
    (toSec ** (power + 1) - fromSec ** (power + 1))
  )
}

function meterBurstRemainingRampDecayCapacity(
  elapsedSec: number,
  maxDecayPerSecond: number,
  rampSec: number,
) {
  return meterBurstRampDecayBetweenSeconds(
    elapsedSec,
    rampSec,
    maxDecayPerSecond,
    rampSec,
  )
}

function meterBurstRampOnlyDrainTargetSec(
  elapsedSec: number,
  value: number,
  maxDecayPerSecond: number,
  rampSec: number,
) {
  const power = METER_BURST_DRAIN_RAMP_POWER
  const rampSecPower = rampSec ** power
  const targetPower =
    elapsedSec ** (power + 1) +
    ((power + 1) * rampSecPower * value) / maxDecayPerSecond
  return targetPower ** (1 / (power + 1))
}

export function getMeterBurstMaxDecayPerSecond(
  capacity: number,
  drain: Pick<
    BogglePlaygroundMeterBurstSettings['drain'],
    'drainRampSeconds' | 'durationSeconds'
  >,
) {
  return capacity / getMeterBurstEffectiveDrainSeconds(drain)
}

export function computeMeterBurstDecayAmount(
  elapsedMs: number,
  stepMs: number,
  maxDecayPerSecond: number,
  rampMs: number,
) {
  if (stepMs <= 0 || maxDecayPerSecond <= 0) {
    return 0
  }

  if (rampMs <= 0) {
    return (maxDecayPerSecond * stepMs) / 1000
  }

  const startMs = elapsedMs
  const endMs = elapsedMs + stepMs
  const rampSec = rampMs / 1000

  const decayBetween = (fromMs: number, toMs: number) => {
    const fromSec = fromMs / 1000
    const toSec = toMs / 1000

    if (fromMs >= rampMs) {
      return (maxDecayPerSecond * (toMs - fromMs)) / 1000
    }

    if (toMs <= rampMs) {
      return meterBurstRampDecayBetweenSeconds(
        fromSec,
        toSec,
        maxDecayPerSecond,
        rampSec,
      )
    }

    const rampPart = meterBurstRampDecayBetweenSeconds(
      fromSec,
      rampSec,
      maxDecayPerSecond,
      rampSec,
    )
    const fullPart = (maxDecayPerSecond * (toMs - rampMs)) / 1000
    return rampPart + fullPart
  }

  return decayBetween(startMs, endMs)
}

export function getMeterBurstDrainTimeRemainingMs(
  value: number,
  elapsedMs: number,
  maxDecayPerSecond: number,
  rampMs: number,
) {
  if (value <= 0 || maxDecayPerSecond <= 0) {
    return 0
  }

  if (rampMs <= 0) {
    return Math.ceil((value / maxDecayPerSecond) * 1000 - 1e-9)
  }

  const rampSec = rampMs / 1000
  const elapsedSec = elapsedMs / 1000

  if (elapsedMs >= rampMs) {
    return Math.ceil((value / maxDecayPerSecond) * 1000 - 1e-9)
  }

  const remainingRampDecayCapacity = meterBurstRemainingRampDecayCapacity(
    elapsedSec,
    maxDecayPerSecond,
    rampSec,
  )

  if (value <= remainingRampDecayCapacity + 1e-9) {
    const targetSec = meterBurstRampOnlyDrainTargetSec(
      elapsedSec,
      value,
      maxDecayPerSecond,
      rampSec,
    )
    return Math.ceil(targetSec * 1000 - elapsedMs - 1e-9)
  }

  const afterRamp = value - remainingRampDecayCapacity
  const fullSpeedMs = (afterRamp / maxDecayPerSecond) * 1000
  return Math.ceil(rampMs - elapsedMs + fullSpeedMs - 1e-9)
}

export function getBoggleMeterSegmentBoundaries(
  settings: Pick<BogglePlaygroundMeterSettings, 'capacity' | 'segments'>,
): number[] {
  const totalSize = settings.segments.reduce((sum, segment) => sum + segment.size, 0)
  if (totalSize <= 0) {
    return []
  }

  const boundaries: number[] = []
  let cumulativeSize = 0
  for (const segment of settings.segments) {
    cumulativeSize += segment.size
    boundaries.push((cumulativeSize / totalSize) * settings.capacity)
  }
  return boundaries
}

export function getBoggleMeterTier(
  value: number,
  settings: Pick<BogglePlaygroundMeterSettings, 'capacity' | 'segments'>,
) {
  let tier = 0
  for (const boundary of getBoggleMeterSegmentBoundaries(settings)) {
    if (value >= boundary) {
      tier += 1
    } else {
      break
    }
  }
  return tier
}

export function getBoggleMeterScoreBonus(
  value: number,
  settings: Pick<BogglePlaygroundMeterSettings, 'capacity' | 'segments'>,
) {
  const tier = getBoggleMeterTier(value, settings)
  return tier <= 0 ? 0 : (settings.segments[tier - 1]?.scoreBonus ?? 0)
}

export function getBoggleMeterAcceptedWordIncrease(
  word: string,
  settings: Pick<BogglePlaygroundMeterSettings, 'increase'>,
  options?: { isRepeat?: boolean },
) {
  const amount = getWordChangeAmount(word, settings.increase.acceptedWord)
  if (!options?.isRepeat) {
    return amount
  }

  return Math.floor(settings.increase.acceptedWord.repeatScalar * amount)
}

function getMeterLetterBonusSets(
  settings: Pick<
    BogglePlaygroundMeterWordChangeSettings,
    'rareLetter' | 'uncommonLetter'
  >,
) {
  const rareLetters = new Set(
    settings.rareLetter.letters.map(letter => letter.toUpperCase()),
  )
  const uncommonLetters = new Set(
    settings.uncommonLetter.letters
      .map(letter => letter.toUpperCase())
      .filter(letter => !rareLetters.has(letter)),
  )

  return { rareLetters, uncommonLetters }
}

export function countBoggleMeterRareLetters(
  word: string,
  settings: Pick<BogglePlaygroundMeterWordChangeSettings, 'rareLetter'>,
) {
  const rareLetters = new Set(
    settings.rareLetter.letters.map(letter => letter.toUpperCase()),
  )
  let rareCount = 0

  for (const letter of word.toUpperCase()) {
    if (rareLetters.has(letter)) {
      rareCount += 1
    }
  }

  return rareCount
}

export function countBoggleMeterUncommonLetters(
  word: string,
  settings: Pick<
    BogglePlaygroundMeterWordChangeSettings,
    'rareLetter' | 'uncommonLetter'
  >,
) {
  const { uncommonLetters } = getMeterLetterBonusSets(settings)
  let uncommonCount = 0

  for (const letter of word.toUpperCase()) {
    if (uncommonLetters.has(letter)) {
      uncommonCount += 1
    }
  }

  return uncommonCount
}

export function applyBoggleMeterDelta(
  meter: BoggleMeterState,
  delta: number,
  settings: Pick<
    BogglePlaygroundMeterSettings,
    'bursts' | 'capacity' | 'enabled' | 'tileSpawn'
  >,
): BoggleMeterState {
  if (!settings.enabled || delta === 0) {
    return meter
  }

  const nextValue = clamp(meter.value + delta, 0, settings.capacity)
  const meterFullBurst = findEnabledMeterBurstByTrigger(settings, 'meter-full')
  const nextBurstActive =
    meter.burstActive ||
    (meterFullBurst !== null && nextValue >= settings.capacity)
  const nextActiveBurstId = nextBurstActive
    ? meter.burstActive
      ? meter.activeBurstId
      : (meterFullBurst?.id ?? meter.activeBurstId)
    : null

  const allowOvermeterIncrease =
    delta > 0 && settings.tileSpawn.enabled && !nextBurstActive
  const overmeterIncrease = allowOvermeterIncrease
    ? Math.max(0, meter.value + delta - settings.capacity)
    : 0

  const nextOvermeterValue = nextBurstActive
    ? 0
    : meter.overmeterValue + overmeterIncrease
  if (
    nextValue === meter.value &&
    nextBurstActive === meter.burstActive &&
    nextActiveBurstId === meter.activeBurstId &&
    nextOvermeterValue === meter.overmeterValue
  ) {
    return meter
  }

  return {
    activeBurstId: nextActiveBurstId,
    burstActive: nextBurstActive,
    burstElapsedMs: nextBurstActive
      ? meter.burstActive
        ? meter.burstElapsedMs
        : 0
      : 0,
    overmeterValue: nextOvermeterValue,
    tileSpawnElapsedMs: meter.tileSpawnElapsedMs,
    value: nextValue,
  }
}

export function decayBoggleMeter(
  meter: BoggleMeterState,
  elapsedMs: number,
  settings: Pick<
    BogglePlaygroundMeterSettings,
    'bursts' | 'capacity' | 'enabled' | 'naturalDecayPerSecond'
  >,
): BoggleMeterState {
  if (!settings.enabled || elapsedMs <= 0) {
    return meter
  }

  const activeBurst = getActiveMeterBurst(settings, meter)
  const rampMs = activeBurst ? getMeterBurstDrainRampMs(activeBurst.drain) : 0
  const maxDecayPerSecond = activeBurst
    ? getMeterBurstMaxDecayPerSecond(settings.capacity, activeBurst.drain)
    : 0

  const decayAmount =
    meter.burstActive && activeBurst
      ? computeMeterBurstDecayAmount(
          meter.burstElapsedMs,
          elapsedMs,
          maxDecayPerSecond,
          rampMs,
        )
      : settings.naturalDecayPerSecond <= 0
        ? 0
        : (elapsedMs / 1000) * settings.naturalDecayPerSecond

  if (decayAmount <= 0) {
    return meter.burstActive
      ? {
          ...meter,
          burstElapsedMs: meter.burstElapsedMs + elapsedMs,
          overmeterValue: 0,
        }
      : meter
  }

  const decayedValue = clamp(meter.value - decayAmount, 0, settings.capacity)
  const nextValue = decayedValue <= 1e-9 ? 0 : decayedValue
  const nextBurstActive =
    meter.burstActive &&
    activeBurst !== null &&
    (nextValue > 0 || activeBurst.trigger === 'timer-end')

  return {
    activeBurstId: nextBurstActive ? meter.activeBurstId : null,
    burstActive: nextBurstActive,
    burstElapsedMs: nextBurstActive ? meter.burstElapsedMs + elapsedMs : 0,
    overmeterValue: meter.burstActive ? 0 : meter.overmeterValue,
    tileSpawnElapsedMs: meter.tileSpawnElapsedMs,
    value: nextValue,
  }
}

const SPAWN_TIMING_EPS = 1e-9

/**
 * Display/readout fractional gold charge `(value + overmeter) / capacity`; unbounded as overmeter grows.
 */
export function getBoggleMeterDisplayedChargeRatio(
  meter: Pick<BoggleMeterState, 'overmeterValue' | 'value'>,
  capacity: number,
): number {
  if (!(capacity > 0)) {
    return 0
  }
  const combined = meter.value + meter.overmeterValue
  return combined > 0 ? combined / capacity : 0
}

/** [0–1] glow weight from spilled overmeter at/above capacity (UI only). */
export function getBoggleMeterOvermeterGlowEfficacy(
  overmeterValue: number,
  capacity: number,
): number {
  if (!(overmeterValue > 0 && capacity > 0)) {
    return 0
  }

  return Math.min(overmeterValue / capacity, 1)
}

export function getBoggleMeterTileSpawnIntervalMs(
  meter: Pick<BoggleMeterState, 'overmeterValue' | 'value'>,
  settings: Pick<BogglePlaygroundMeterSettings, 'capacity' | 'tileSpawn'>,
): number | null {
  const tileSpawn = settings.tileSpawn
  if (!tileSpawn?.enabled || !(settings.capacity > 0)) {
    return null
  }

  const chargeRatio = getBoggleMeterDisplayedChargeRatio(meter, settings.capacity)
  if (!(chargeRatio > 0)) {
    return null
  }

  const intervalSec =
    tileSpawn.baseIntervalSeconds * chargeRatio ** -tileSpawn.powerExponent
  if (!(intervalSec > SPAWN_TIMING_EPS)) {
    return Math.max(SPAWN_TIMING_EPS, intervalSec) * 1000
  }

  return intervalSec * 1000
}

function getWordChangeAmount(
  word: string,
  settings: BogglePlaygroundMeterWordChangeSettings,
) {
  const matchingBand = settings.lengthBands.find(
    band =>
      word.length >= band.minLength &&
      (band.maxLength === null || word.length <= band.maxLength),
  )
  const lengthContribution = matchingBand
    ? getLengthBandChangeAmount(word.length, matchingBand)
    : getLinearWordChangeAmount(word.length, settings)
  const uncommonCount = countBoggleMeterUncommonLetters(word, settings)
  const rareCount = countBoggleMeterRareLetters(word, settings)

  return (
    lengthContribution +
    uncommonCount * settings.uncommonLetter.perOccurrence +
    rareCount * settings.rareLetter.perOccurrence
  )
}

function getLengthBandChangeAmount(
  wordLength: number,
  settings: Pick<
    BogglePlaygroundMeterWordChangeSettings,
    'base' | 'letterOffset' | 'perLetter'
  >,
) {
  return (
    settings.base +
    getCountedLetters(wordLength, settings.letterOffset) * settings.perLetter
  )
}

function getLinearWordChangeAmount(
  wordLength: number,
  settings: BogglePlaygroundMeterWordChangeSettings,
) {
  return getLengthBandChangeAmount(wordLength, settings)
}

function getCountedLetters(wordLength: number, letterOffset: number) {
  return Math.max(0, wordLength + letterOffset)
}

function clamp(value: number, min: number, max: number) {
  if (value < min) {
    return min
  }
  if (value > max) {
    return max
  }
  return value
}
